// src/pages/LitSymbolSection.jsx
//
// Student-facing render of a SYMBOL lit_section (sectionKind 'symbol'). Faithful port of
// the prototype LitSymbolView (between-the-lines-v2.html L12795-12869): a 4-tab view --
// Meaning / Timeline / Analysis / Theme Connection -- using the prototype's real markup.
//
// Reads the flat SYMBOL_BLOCKS (litSectionShape.js, mirrors LitSymbolEditor):
// blocks.meaning (longText), blocks.timeline ([{when,phrase,note}]),
// blocks.analyses ([{ref,close,comment}]), blocks.themes ([{theme,link}]).
//
// Pure presentational (takes `content`). Deferred like the Overview port: glossary
// highlighting (renderLitText -> plain text), Export PDF, podcast player, and the quiz tab.
import React, { useState } from 'react';
import AnnotatedText from '../components/AnnotatedText.jsx';
import LessonHeader from '../components/reader/LessonHeader.jsx';

const TABS = [
    ['meaning', 'Meaning'],
    ['timeline', 'Timeline'],
    ['analysis', 'Analysis'],
    ['themes', 'Theme Connection'],
];

function EmptyNote() {
    return <p className="font-serif italic text-book-muted text-sm">Nothing here yet.</p>;
}

export default function LitSymbolSection({ content, done = false }) {
    const blocks = (content && content.blocks) || {};
    const meaning = typeof blocks.meaning === 'string' ? blocks.meaning : '';
    const timeline = Array.isArray(blocks.timeline) ? blocks.timeline : [];
    const analyses = Array.isArray(blocks.analyses) ? blocks.analyses : [];
    const themes = Array.isArray(blocks.themes) ? blocks.themes : [];
    const [tab, setTab] = useState('meaning');

    return (
        <div className="max-w-3xl mx-auto pb-8">
            <LessonHeader eyebrow="Symbol" title={(content && content.title) || 'Symbol'}
                blocks={blocks} done={done} />

            <div className="inline-flex border border-book-accent/40 mb-6 rounded-sm overflow-hidden flex-wrap">
                {TABS.map(([k, l]) => (
                    <button key={k} onClick={() => setTab(k)}
                        className={`px-4 py-2 font-display text-xs transition-colors font-semibold ${tab === k ? 'bg-book-accent text-book-pageDark' : 'text-book-muted hover:text-book-accent'}`}>
                        {l}
                    </button>
                ))}
            </div>

            {tab === 'meaning' && (
                meaning
                    ? <p className="font-serif text-book-text text-base leading-relaxed whitespace-pre-wrap"><AnnotatedText>{meaning}</AnnotatedText></p>
                    : <EmptyNote />
            )}

            {tab === 'timeline' && (
                timeline.length > 0 ? (
                    <div className="space-y-3">
                        <p className="font-serif italic text-book-muted mb-2">Every recurrence of the symbol across the play.</p>
                        {timeline.map((t, i) => (
                            <div key={i} className="rounded-[var(--radius-card)] border border-book-line bg-book-page shadow-[var(--shadow-card)] p-4 flex items-start gap-4">
                                <div className="w-20 flex-shrink-0 font-display text-book-accent text-xs text-center py-2 border-r border-book-accent/30 font-semibold">{t && t.when}</div>
                                <div className="flex-grow">
                                    <div className="font-display text-book-text text-sm mb-1">"{t && t.phrase}"</div>
                                    <p className="font-serif italic text-book-muted text-sm leading-relaxed whitespace-pre-wrap">{t && t.note}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : <EmptyNote />
            )}

            {tab === 'analysis' && (
                analyses.length > 0 ? (
                    <div className="space-y-4">
                        {analyses.map((a, i) => (
                            <div key={i} className="border-l-4 border-book-accent bg-book-pageDark/40 p-5">
                                <div className="font-display text-book-accent text-xs mb-2 font-semibold">{a && a.ref}</div>
                                <p className="font-serif italic text-book-text text-base leading-relaxed mb-3">"{a && a.close}"</p>
                                <p className="font-serif text-book-text/85 leading-relaxed whitespace-pre-wrap">→ <AnnotatedText>{a && a.comment}</AnnotatedText></p>
                            </div>
                        ))}
                    </div>
                ) : <EmptyNote />
            )}

            {tab === 'themes' && (
                themes.length > 0 ? (
                    <div className="grid md:grid-cols-2 gap-3">
                        {themes.map((t, i) => (
                            <div key={i} className="border border-book-accent/30 bg-book-accent/5 p-5 swp-corner text-book-accent">
                                <div className="font-display text-book-accent text-sm mb-2 font-semibold">{t && t.theme}</div>
                                <p className="font-serif text-book-text leading-relaxed whitespace-pre-wrap"><AnnotatedText>{t && t.link}</AnnotatedText></p>
                            </div>
                        ))}
                    </div>
                ) : <EmptyNote />
            )}
        </div>
    );
}
